<template>
  <base-modal :ref="modal.key" :show="open" maxWidth="800" :title="modal.title" @on-confirm="saveItem"
    @on-cancel="hide()" :okText="$t('Save')" :closeText="$t('Close')">
    <v-form class="mt-5 comment-section mb-5 pb-5 pt-0">
      <comment-users-list ref="commentUsersList" class="comment-users-list" @userTagged="tagUser"
        v-show="showUsersList" />

      <tiptap-vuetify v-model="comment.text" :extensions="extensions" @keydown="keydownHandler" class="pb-0 mb-5"
        :placeholder="$t('Use @ to mention users.')" />

      <v-row align="center">
        <v-col class="text-center" cols="2">
          <v-text-field style="position: absolute" v-model="comment.pageNumber" min="1" type="number"
            v-bind:label="$t('Page')" :error-messages="pageNumberErrors" @input="$v.comment.pageNumber.$touch()"
            @blur="$v.comment.pageNumber.$touch()" class="mb-2" />
        </v-col>
      </v-row>
    </v-form>
  </base-modal>
</template>

<script>
import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify';
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import CommentService from '@/services/CommentService';
import Comment from '@/ecm/models/search/results/list/Comment';
import Utils from '@/ecm/common/Utils';
import CommentUsersList from './CommentUsersList.vue';
import UserService from '@/services/UserService';

export default {
    name: 'CommentModal',
    components: {
        TiptapVuetify,
        CommentUsersList,
    },
    data() {
        return {
            comment: new Comment(),
            modal: {
                title: this.$t('Comments'),
                key: 'commentModal',
            },
            usersTagged: [],
            userTagSearchText: '',
            organizationUsers: [],
            showUsersList: false,
            open: false,
            altPressed: false,
            sixPressed: false,
            extensions: [
                History,
                Blockquote,
                Link,
                Underline,
                Strike,
                Italic,
                ListItem,
                BulletList,
                OrderedList,
                [
                    Heading,
                    {
                        options: {
                            levels: [1, 2, 3],
                        },
                    },
                ],
                Bold,
                HorizontalRule,
                Paragraph,
                HardBreak,
            ],
        };
    },
    mixins: [validationMixin],
    validations: {
        comment: {
            pageNumber: { required },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        pageNumberErrors() {
            const errors = [];
            if (!this.$v.comment.pageNumber.$dirty) return errors;
            !this.$v.comment.pageNumber.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    methods: {
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.modalInstance.progress(true);
            CommentService.save(this.comment.toDto())
                .then((result) => {
                    if (result) {
                        Utils.toast.success(this.$t('AddCommentSuccess'));
                        this.hide();
                        this.$emit('on-comment-saved');
                    }
                }).catch({})
                .finally(() => {
                    this.modalInstance.progress(false);
                });
        },
        new(documentId) {
            this.comment = new Comment();
            this.comment.pageNumber = 1;
            this.comment.documentId = documentId;
            this.$v.$reset();
            this.show();
        },
        edit(comment) {
            this.comment = Utils.object.clone(new Comment(), comment);
            this.show();
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
            this.showUsersList = false;
        },
        keydownHandler(keydown) {
            switch (keydown.key) {
            case '@':
                this.showUsersList = true;
                this.userTagSearchText = '';
                break;

            case 'Alt':
                this.altPressed = true;
                break;

            case '6':
                if (this.altPressed) {
                    this.sixPressed = true;
                }
                break;

            case '4':
                if (this.altPressed && this.sixPressed) {
                    this.showUsersList = true;
                    this.userTagSearchText = '';
                    this.sixPressed = false;
                    this.altPressed = false;
                }
                break;

            case ' ':
                if (this.showUsersList) {
                    this.userTagSearchText = '';
                    this.showUsersList = false;
                    this.sixPressed = false;
                    this.altPressed = false;
                }
                break;

            case 'Backspace':
                if (this.userTagSearchText.length > 0) {
                    this.userTagSearchText = this.userTagSearchText.slice(0, -1);
                    this.$refs.commentUsersList.filterUsers(this.userTagSearchText);
                } else {
                    this.userTagSearchText = '';
                    this.showUsersList = false;
                    this.sixPressed = false;
                    this.altPressed = false;
                }
                break;

            default:
                if (this.showUsersList && keydown.key !== '@' && this.isLetter(keydown.code)) {
                    this.userTagSearchText += keydown.key;
                    this.$refs.commentUsersList.filterUsers(this.userTagSearchText);
                    this.sixPressed = false;
                    this.altPressed = false;
                }
                break;
            }
        },
        tagUser(userTagged) {
            // We pass the id inside the href since it's the only element that tiptap-vuetify doesn't remove
            const formattedUser = `<a href="#${userTagged.id}">@${userTagged.name} ${userTagged.surname}</a>`;

            if (this.userTagSearchText == '') {
                // Regular expression to find the last '@' followed by a whitespace or the end of the string
                const regex = /<a[^>]*>(?:(?!<\/a>).)*<\/a>|@(?![^<]*<\/a>)/g;
                const matches = Array.from(this.comment.text.matchAll(regex));
                const lastIndex =
                    matches.length > 0 ? matches[matches.length - 1].index : -1;
                this.comment.text =
                    this.comment.text.substring(0, lastIndex) +
                    formattedUser +
                    this.comment.text.substring(lastIndex + 1);
            } else {
                // Regular expression to find the '@' with the value in the text and replace it with the new user
                const regex = new RegExp(
                    `(?<!<a[^>]*>)@${this.userTagSearchText}(?!</a>)`,
                    'g'
                );
                this.comment.text = this.comment.text.replace(regex, formattedUser);
            }

            this.userTagSearchText = '';
            this.showUsersList = false;
        },
        isLetter(key) {
            const regex = /^Key.*/;
            return regex.test(key);
        },
        getUsers() {
            UserService.getList()
                .then((response) => {
                    this.organizationUsers = response;
                })
                .catch(() => { });
        },
    },
    watch: {
        'comment.text': {
            handler(newValue) {
                if (this.showUsersList && newValue != '@') {
                    this.$refs.commentUsersList.filterUsers(this.userTagSearchText);
                }
            },
            deep: true,
        },
        showUsersList: {
            handler(e) {
                if (e) {
                    this.$refs.commentUsersList.loadUsers(this.organizationUsers);
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.getUsers();
    }
};
</script>

<style lang="scss">
.comment-section {
  position: relative;
  .comment-users-list {
    position: absolute;
    width: 100%;
    z-index: 999;
    top: calc(40%);
  }
  .tiptap-vuetify-editor__content {
    height: 130px !important;
  }
  .v-input__control {
    height: 20px;
  }
}
</style>
